import { Select } from 'antd';

import { AtlasGqlInspectionSiteType, useGetInspectionTypesQuery } from 'types/atlas-graphql';
import {
  AUTONOMOUS_DRONE,
  BLADE_INTERNAL_LEGACY,
  EXTERNAL_BLADE_INSPECTION_TYPES,
  GEARBOX_BORESCOPE,
  GENERIC,
  isGenericInspection,
  isSolarInspection,
  isWindInspection,
  NACELLE_EXTERNAL,
  TOWER_EXTERNAL,
  TRANSITION_PIECE,
} from 'utils/constants';
import { useFeatures } from 'utils/features';
interface Props {
  assetTypeId?: string;
  vendorId?: string;
  onChange: (value: any) => void;
  defaultValue?: string;
  placeholder: string;
  disabled: boolean;
  dropdownStyle?: object;
  onlyExternal?: boolean;
  siteType?: AtlasGqlInspectionSiteType;
  useBackendInspectionTypesOnly?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

export const InspectionTypeChooser = ({
  assetTypeId,
  onChange,
  disabled,
  vendorId,
  onlyExternal,
  siteType = AtlasGqlInspectionSiteType.Wind,
  useBackendInspectionTypesOnly = false,
  ...props
}: Props) => {
  const { features } = useFeatures();

  const filters = [];
  if (assetTypeId) {
    filters.push({ key: 'assetTypeId', values: [assetTypeId] });
  }
  if (vendorId) {
    filters.push({ key: 'vendorId', values: [vendorId || null] });
  }
  if (onlyExternal) {
    filters.push({ key: 'name', values: EXTERNAL_BLADE_INSPECTION_TYPES });
  }

  const { data: inspectionTypeData, loading: inspectionTypesLoading } = useGetInspectionTypesQuery({
    variables: {
      siteType: siteType,
      filterBy: filters,
    },
    skip: !assetTypeId && !vendorId,
  });

  const dropdownAssetTypes = inspectionTypeData?.inspectionTypes;

  const options = dropdownAssetTypes
    ?.filter(({ name }: { name: string }) => {
      if (useBackendInspectionTypesOnly) {
        return true;
      }

      if (siteType === AtlasGqlInspectionSiteType.Solar) {
        return (
          (isSolarInspection(name) || isGenericInspection(name)) &&
          (name !== GENERIC || features['FLEET_MANAGEMENT'])
        );
      } else {
        return (
          (isWindInspection(name) || isGenericInspection(name)) &&
          // disallow the option of creating Blade Internal Legacy inspections
          name !== BLADE_INTERNAL_LEGACY &&
          // Nacelle External inspections are always created through ops ingestion. So always hide this option
          name !== NACELLE_EXTERNAL &&
          (name !== AUTONOMOUS_DRONE || features['AUTONOMOUS_DRONE_UPLOAD']) &&
          (name !== TRANSITION_PIECE || features['TP_UPLOAD']) &&
          (name !== TOWER_EXTERNAL || features['TOWER_UPLOAD']) &&
          (name !== GEARBOX_BORESCOPE || features['GEARBOX_BORESCOPE']) &&
          (name !== GENERIC || features['FLEET_MANAGEMENT'])
        );
      }
    })
    .map(({ id, name }: { id: string; name: string }) => ({ label: name, value: id }));

  return (
    <Select
      data-testid="inspection-type-chooser"
      options={options}
      disabled={disabled}
      loading={inspectionTypesLoading}
      onChange={onChange}
      {...props}
    />
  );
};
